/**
 * Normalize terms for RichText / HTML preview. Handles object-shaped values and
 * the legacy "[object Object]" string stored when sync cast objects incorrectly.
 */
const SYMBOL_BULLETS = "•‣◦⁃∙・▪●○◆※";

function normalizeTermsTextSegment(segment) {
  let text = String(segment || "")
    .replace(/\r\n?/g, "\n");
  const symbolBreak = new RegExp(
    `([^\\n])[ \\t]+(?=[${SYMBOL_BULLETS}](?:[ \\t]+|$))`,
    "g",
  );
  text = text.replace(symbolBreak, "$1\n");

  const numberedMarkers = [...text.matchAll(
    /(?:^|[ \t])(?:\((\d{1,2})\)|(\d{1,2})[.)])[ \t]+/g,
  )].map((match) => Number(match[1] || match[2]));
  if (numberedMarkers.some(
    (number, index) => number === 1 && numberedMarkers[index + 1] === 2,
  )) {
    text = text.replace(
      /([^\n])[ \t]+(?=(?:\(\d{1,2}\)|\d{1,2}[.)])[ \t]+)/g,
      "$1\n",
    );
  }
  return text;
}

export function normalizeTermsLayout(value) {
  if (value == null || value === "") return "";
  let terms = String(value).replace(/<\s*\/\s*br\s*>/gi, "<br>");
  terms = terms
    .split(/(<[^>]*>)/g)
    .map((part) => (part.startsWith("<") ? part : normalizeTermsTextSegment(part)))
    .join("");
  const bulletIndent = new RegExp(
    `\n[ \\t]+(?=(?:[${SYMBOL_BULLETS}]|\\(\\d{1,2}\\)|\\d{1,2}[.)])(?:[ \\t]+|$))`,
    "g",
  );
  return terms
    .replace(/[ \t]+\n/g, "\n")
    .replace(bulletIndent, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function coerceTermsForDisplay(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    const hasTerms =
      Object.prototype.hasOwnProperty.call(value, "terms_and_conditions_en") ||
      Object.prototype.hasOwnProperty.call(value, "terms_and_conditions_th");
    if (hasTerms) {
      const en = value.terms_and_conditions_en;
      const th = value.terms_and_conditions_th;
      if (typeof en === "string" && en.trim()) return en;
      if (typeof th === "string" && th.trim()) return th;
      if (en != null && typeof en !== "string") return String(en);
      if (th != null && typeof th !== "string") return String(th);
      return "";
    }
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "[object Object]") return "";
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((x) => coerceTermsForDisplay(x))
      .filter((x) => typeof x === "string" && x.trim())
      .join("\n");
  }
  if (typeof value === "object") {
    const parts = [];
    const walk = (x, depth) => {
      if (depth > 12 || x == null) return;
      if (typeof x === "string" && x.trim()) parts.push(x.trim());
      else if (typeof x === "number" || typeof x === "boolean") parts.push(String(x));
      else if (Array.isArray(x)) for (const y of x) walk(y, depth + 1);
      else if (typeof x === "object") for (const y of Object.values(x)) walk(y, depth + 1);
    };
    walk(value, 0);
    if (parts.length > 0) return [...new Set(parts)].join("\n");
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return "";
}

export function normalizeTermsForDisplay(value) {
  return normalizeTermsLayout(coerceTermsForDisplay(value));
}

/** Description: bilingual blobs (`description_en` / `description_th`) → English only. */
export function normalizeDescriptionForDisplay(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    const hasDesc =
      Object.prototype.hasOwnProperty.call(value, "description_en") ||
      Object.prototype.hasOwnProperty.call(value, "description_th");
    if (hasDesc) {
      const en = value.description_en;
      return typeof en === "string" ? normalizeTermsLayout(en) : "";
    }
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "[object Object]") return "";
    return normalizeTermsLayout(value);
  }
  return normalizeTermsForDisplay(value);
}

/**
 * Plain text extracted from HTML (for empty checks). Uses DOM when available.
 */
export function stripHtmlToPlainText(html) {
  if (html == null || html === "") return "";
  const s = typeof html === "string" ? html : String(html);
  if (!s.trim()) return "";
  if (typeof document === "undefined") {
    return s
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#?\w+;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = s;
  return (tmp.textContent || tmp.innerText || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** True if HTML/rich text has any visible text (e.g. empty Quill `<p><br></p>` → false). */
export function hasVisibleHtmlContent(html) {
  return stripHtmlToPlainText(html).length > 0;
}
